// 로케일에 따라 달라지는 표시 형식 — 단일 진입점. LiveSQL(frontend/src/utils/formatters.js)과 같은 원칙.
//
// [왜 한곳으로 모으나]
// 예전에는 toLocaleString() · toLocaleTimeString('ko-KR') · 손으로 조립한 'YYYY-MM-DD' 가 화면마다 흩어져,
// 같은 시각이 창마다 다르게 보였다("2026. 9. 17. 오후 5:43:35" 와 "2026-09-17 17:43"). 언어를 바꿔도
// 단위("초", "건")는 한국어로 남았다. 여기서 현재 언어를 읽어 Intl 에 넘기므로 한 번에 따라온다.
//
// [날짜는 숫자 모양(YYYY-MM-DD HH:mm:ss)을 유지한다]
// 실행 목록·이력 표처럼 좁은 칸에 시각이 많이 들어간다. "2026. 09. 17. 오후 05:43:35" 로 바꾸면 칸이 넘친다.
// 숫자 모양은 어느 언어에서도 읽히므로 모양은 고정한다.
//
// [시간대는 브라우저가 아니라 프로젝트의 시간대다] (utils/timeZone)
// 예전에는 보는 사람의 브라우저 시간대로 바꿨다. 그러면 같은 실행 시각이 PC 마다 다르게 보이고, 스케줄
// 시각(서버가 해석하는 시간대)과도 어긋난다. 이제 시스템 기본(한국시간) 또는 프로젝트가 정한 시간대로 그린다.
// 서버·Airflow 는 UTC ISO 문자열을 준다. 시간대 표시가 없는 문자열(QBase 의 등록일시 등)은 **서버가 있는
// 곳의 시각**, 곧 시스템 기본 시간대로 읽는다.
import i18n from '../i18n';
import { effectiveTimeZone, systemTimeZone } from './timeZone';

/** 현재 언어의 BCP 47 로케일 */
export function currentLocale(): string {
  return (i18n.language || 'ko').startsWith('en') ? 'en-US' : 'ko-KR';
}

/** 숫자에 천 단위 구분 기호. null/빈값은 dash */
export function formatNumber(value: unknown, { dash = '-', ...opts }: Intl.NumberFormatOptions & { dash?: string } = {}): string {
  if (value == null || value === '') return dash;
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return new Intl.NumberFormat(currentLocale(), opts).format(n);
}

/** 건수 — "1,234건" / "1,234 rows" */
export function formatRows(count: number): string {
  return i18n.t('common:unit.rows', { n: formatNumber(count) });
}

/** 용량 — "512 MB" / "1.8 GB" / "465 GB". 단위 기호는 어느 언어에서도 같다 (1024 단위) */
export function formatBytes(bytes: number | null | undefined, { dash = '-' }: { dash?: string } = {}): string {
  if (bytes == null || !Number.isFinite(bytes)) return dash;
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let v = Math.max(0, bytes);
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${formatNumber(v, { maximumFractionDigits: v < 10 && i > 0 ? 1 : 0 })} ${units[i]}`;
}

type DateInput = string | number | Date | null | undefined;

/** 시간대 표시가 없는 날짜·시각 문자열 — 'YYYY-MM-DD', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DDTHH:mm:ss.sss' */
const NAIVE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?)?$/;

const partFormatters = new Map<string, Intl.DateTimeFormat>();
/** 그 시간대의 연·월·일·시·분·초 */
function partsIn(d: Date, tz: string): { y: number; mo: number; da: number; h: number; mi: number; s: number } {
  let f = partFormatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    partFormatters.set(tz, f);
  }
  const o: Record<string, string> = {};
  for (const part of f.formatToParts(d)) o[part.type] = part.value;
  return { y: +o.year, mo: +o.month, da: +o.day, h: +o.hour % 24, mi: +o.minute, s: +o.second };
}

/** 벽시계 시각(그 시간대 기준) → 실제 순간. 서머타임 경계도 한 번 더 맞춘다 */
function zonedToDate(y: number, mo: number, da: number, h: number, mi: number, s: number, ms: number, tz: string): Date {
  const wall = Date.UTC(y, mo - 1, da, h, mi, s, ms);
  let guess = wall;
  for (let i = 0; i < 2; i++) {
    const p = partsIn(new Date(guess), tz);
    const shown = Date.UTC(p.y, p.mo - 1, p.da, p.h, p.mi, p.s, ms);
    guess += wall - shown;
  }
  return new Date(guess);
}

function toDate(value: DateInput): Date | null {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const m = NAIVE.exec(value.trim());
    if (m) {
      const [, y, mo, da, h = '0', mi = '0', s = '0', frac = '0'] = m;
      return zonedToDate(+y, +mo, +da, +h, +mi, +s, Math.round(+`0.${frac}` * 1000), systemTimeZone());
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** 프로젝트 시간대로 'YYYY-MM-DD HH:mm(:ss)' — 모양은 언어와 무관하게 고정(위 설명) */
export function formatDateTime(value: DateInput, { dash = '-', seconds = true }: { dash?: string; seconds?: boolean } = {}): string {
  const d = toDate(value);
  if (!d) return value == null || value === '' ? dash : String(value);
  const p = partsIn(d, effectiveTimeZone());
  return `${p.y}-${pad(p.mo)}-${pad(p.da)} ${pad(p.h)}:${pad(p.mi)}${seconds ? `:${pad(p.s)}` : ''}`;
}

/** 'MM-DD HH:mm:ss' — 한 화면 안에서 날짜가 거의 같은 로그 목록용 */
export function formatShortDateTime(value: DateInput, { dash = '-' }: { dash?: string } = {}): string {
  const d = toDate(value);
  if (!d) return value == null || value === '' ? dash : String(value);
  const p = partsIn(d, effectiveTimeZone());
  return `${pad(p.mo)}-${pad(p.da)} ${pad(p.h)}:${pad(p.mi)}:${pad(p.s)}`;
}

/** 'HH:mm:ss' */
export function formatTime(value: DateInput, { dash = '-' }: { dash?: string } = {}): string {
  const d = toDate(value);
  if (!d) return value == null || value === '' ? dash : String(value);
  const p = partsIn(d, effectiveTimeZone());
  return `${pad(p.h)}:${pad(p.mi)}:${pad(p.s)}`;
}

/** 지금 쓰는 시간대 이름 — 화면에 '(Asia/Seoul)' 처럼 적을 때 */
export function formatTimeZoneLabel(): string {
  return effectiveTimeZone();
}

/** 소요시간 — 0.4초 / 12초 / 3분 5초 / 1시간 2분 (영어: 0.4s / 12s / 3m 5s / 1h 2m) */
export function formatDuration(sec: number): string {
  const t = i18n.t.bind(i18n);
  if (sec < 10) return t('common:unit.secondsShort', { value: formatNumber(Math.round(sec * 10) / 10) });
  const s = Math.round(sec);
  if (s < 60) return t('common:unit.secondsShort', { value: formatNumber(s) });
  if (s < 3600) {
    const m = Math.floor(s / 60);
    return s % 60 ? t('common:unit.minutesSeconds', { m, s: s % 60 }) : t('common:unit.minutes', { m });
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m ? t('common:unit.hoursMinutes', { h, m }) : t('common:unit.hours', { h });
}

/** 달력 머리 — "2026년 9월" / "September 2026" */
export function formatYearMonth(year: number, monthIndex: number): string {
  return new Intl.DateTimeFormat(currentLocale(), { year: 'numeric', month: 'long' }).format(new Date(year, monthIndex, 1));
}

/** 요일 짧은 이름 — 일요일부터 ("일 월 …" / "Sun Mon …") */
export function weekdayNames(): string[] {
  const fmt = new Intl.DateTimeFormat(currentLocale(), { weekday: 'short' });
  // 2023-01-01 은 일요일이다
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2023, 0, 1 + i)));
}
